import {
  AssignmentOutlined as AssignmentIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon,
  PictureAsPdf as PdfIcon,
  RemoveCircleOutline as MissingIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { pdf } from "@react-pdf/renderer";
import QRCode from "qrcode";
import { useCallback, useEffect, useState } from "react";
import empreendimentoService from "../services/empreendimento";
import isencaoLicenciamentoConsultaService from "../services/isencaoLicenciamentoConsulta";
import pdfLayoutService from "../services/pdfLayout";
import autorizacoesService from "../services/autorizacoes";
import { statusIsencaoEnum, statusIsencaoLabels } from "../utils/constants";
import { parseAssinaturaSecretarioResponse } from "../utils/assinaturaSecretario";
import CharCounter from "./CharCounter";
import IsencaoLicenciamentoDocument from "./IsencaoLicenciamentoDocument";
import NumeroProcessoField, {
  ehNumeroInformadoValido,
  montarNumeroProcessoPreview,
} from "./NumeroProcessoField";
import PagamentosSection, { PAPEL_ANALISTA } from "./PagamentosSection";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";

const PREFIXO_ISENCAO = "SI";
const STATUS_COLORS = {
  0: "default",
  1: "info",
  2: "success",
  3: "error",
  4: "warning",
  5: "info",
  6: "secondary",
  7: "success",
};

const DOCUMENTOS = [
  { key: "declaracaoS3Key", tipo: "declaracao", label: "Declaração" },
  { key: "oficioS3Key", tipo: "oficio", label: "Ofício" },
  { key: "documentacaoInteressadoS3Key", tipo: "documentacaoInteressado", label: "Documentação do Interessado" },
  { key: "comprovanteEnderecoS3Key", tipo: "comprovanteEndereco", label: "Comprovante de Endereço" },
  { key: "declaracaoMeMeiS3Key", tipo: "declaracaoMeMei", label: "Declaração ME/MEI" },
];

const MOTIVO_MAX = 1000;

const STATUS_DECISAO_PERMITIDOS = [
  statusIsencaoEnum.SOLICITADO,
  statusIsencaoEnum.EM_ANALISE,
  statusIsencaoEnum.AGUARDANDO_PAGAMENTO,
  statusIsencaoEnum.PAGAMENTO_EFETUADO,
];

function IsencaoAnalistaModal({ open, onClose, isencao, onStatusAtualizado }) {
  const [loadingTipo, setLoadingTipo] = useState(null);
  const [mostrarIndeferimento, setMostrarIndeferimento] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [erroMotivo, setErroMotivo] = useState("");
  const [processando, setProcessando] = useState(false);
  const [emitindo, setEmitindo] = useState(false);
  const [baixandoEmitida, setBaixandoEmitida] = useState(false);
  const [atividade, setAtividade] = useState("");
  const [statusPagamento, setStatusPagamento] = useState("");
  const [numeroInformado, setNumeroInformado] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const anoProcesso = new Date().getFullYear();

  useEffect(() => {
    if (!open) return;
    setMostrarIndeferimento(false);
    setMotivo("");
    setErroMotivo("");
    setAtividade(isencao?.atividade || "");
    setStatusPagamento("");
    setNumeroInformado("");

    if (isencao?.id && !isencao?.atividade) {
      isencaoLicenciamentoConsultaService
        .obterPorId(isencao.id)
        .then((detalhes) => setAtividade(detalhes?.atividade || ""))
        .catch(() => {});
    }
  }, [open, isencao?.id, isencao?.atividade]);

  const handleVisualizarDocumento = async (tipo) => {
    setLoadingTipo(tipo);
    try {
      const data = await isencaoLicenciamentoConsultaService.baixarDocumento(isencao.id, tipo);
      const byteArray = Uint8Array.from(atob(data.conteudo), (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: data.contentType || "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setSnackbar({ open: true, message: "Erro ao abrir documento.", severity: "error" });
    } finally {
      setLoadingTipo(null);
    }
  };

  const handleDeferir = async () => {
    setProcessando(true);
    try {
      await isencaoLicenciamentoConsultaService.atualizarStatus(isencao.id, statusIsencaoEnum.DEFERIDO);
      setSnackbar({ open: true, message: "Isenção deferida com sucesso!", severity: "success" });
      onStatusAtualizado?.();
    } catch (err) {
      const msg = err?.response?.data?.message || "Erro ao deferir isenção.";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setProcessando(false);
    }
  };

  const handleIndeferir = async () => {
    if (!motivo || motivo.trim().length < 10) {
      setErroMotivo("O motivo deve ter pelo menos 10 caracteres.");
      return;
    }
    if (motivo.length > MOTIVO_MAX) {
      setErroMotivo(`O motivo não pode exceder ${MOTIVO_MAX} caracteres.`);
      return;
    }
    setErroMotivo("");
    setProcessando(true);
    try {
      await isencaoLicenciamentoConsultaService.atualizarStatus(isencao.id, statusIsencaoEnum.INDEFERIDO, motivo);
      setSnackbar({ open: true, message: "Isenção indeferida.", severity: "info" });
      setMostrarIndeferimento(false);
      setMotivo("");
      onStatusAtualizado?.();
    } catch (err) {
      const msg = err?.response?.data?.message || "Erro ao indeferir isenção.";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setProcessando(false);
    }
  };

  const handleAtualizarStatusPagamento = async () => {
    if (statusPagamento === "" || statusPagamento === null || statusPagamento === undefined) {
      setSnackbar({ open: true, message: "Selecione um status de pagamento.", severity: "warning" });
      return;
    }
    setProcessando(true);
    try {
      await isencaoLicenciamentoConsultaService.atualizarStatus(isencao.id, Number(statusPagamento));
      setSnackbar({
        open: true,
        message: "Status de pagamento atualizado com sucesso!",
        severity: "success",
      });
      setStatusPagamento("");
      onStatusAtualizado?.();
    } catch (err) {
      const msg = err?.response?.data?.message || "Erro ao atualizar status.";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setProcessando(false);
    }
  };

  const carregarPdfLayout = useCallback(async () => {
    try {
      const res = await pdfLayoutService.obterLatest();
      const layoutId = res.data?.data;

      const toDataUrl = (blob) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });

      const [headerRes, footerRes, assinaturaRes] = await Promise.all([
        layoutId ? pdfLayoutService.getHeaderImage(layoutId).catch(() => null) : Promise.resolve(null),
        layoutId ? pdfLayoutService.getFooterImage(layoutId).catch(() => null) : Promise.resolve(null),
        autorizacoesService.getAssinaturaPadrao().catch(() => null),
      ]);

      const assinatura = await parseAssinaturaSecretarioResponse(assinaturaRes);

      return {
        headerImgSrc: headerRes ? await toDataUrl(headerRes.data) : null,
        footerImgSrc: footerRes ? await toDataUrl(footerRes.data) : null,
        assinaturaDataUrl: assinatura.cadastrada ? assinatura.dataUrl : null,
        assinaturaMensagem: assinatura.cadastrada ? null : assinatura.message,
      };
    } catch {
      return {
        headerImgSrc: null,
        footerImgSrc: null,
        assinaturaDataUrl: null,
        assinaturaMensagem: null,
      };
    }
  }, []);

  const handleEmitir = async () => {
    if (!isencao) return;

    if (!isencao.numeroProcesso && !ehNumeroInformadoValido(numeroInformado)) {
      setSnackbar({
        open: true,
        message: "Informe um número de processo válido (apenas dígitos).",
        severity: "error",
      });
      return;
    }

    setEmitindo(true);
    try {
      const [
        dadosCompletos,
        { headerImgSrc, footerImgSrc, assinaturaDataUrl, assinaturaMensagem },
      ] = await Promise.all([
        empreendimentoService.obter(isencao.empreendimentoId),
        carregarPdfLayout(),
      ]);

      let atividadeAtual = atividade || isencao.atividade || "";
      if (!atividadeAtual && isencao?.id) {
        const detalhes = await isencaoLicenciamentoConsultaService
          .obterPorId(isencao.id)
          .catch(() => null);
        atividadeAtual = detalhes?.atividade || "";
      }
      const atividadeLabel = atividadeAtual || "Não informado";

      const enderecoSource = dadosCompletos?.enderecoAtividade || dadosCompletos?.endereco;
      const enderecoCompleto = enderecoSource
        ? `${enderecoSource.rua || ""} ${enderecoSource.numero || ""}, ${enderecoSource.municipio || ""} - ${enderecoSource.estado || ""}`.trim()
        : "Não informado";

      const numeroProcesso =
        isencao.numeroProcesso ||
        montarNumeroProcessoPreview(PREFIXO_ISENCAO, numeroInformado, anoProcesso);

      const qrUrl = `${window.location.origin}/validar/isencao/${isencao.id}`;
      const qrCodeDataUrl = await QRCode.toDataURL(qrUrl, { margin: 1 });

      const doc = (
        <IsencaoLicenciamentoDocument
          empreendimento={dadosCompletos}
          atividadeLabel={atividadeLabel}
          endereco={enderecoCompleto}
          numeroProcesso={numeroProcesso}
          headerImgSrc={headerImgSrc}
          footerImgSrc={footerImgSrc}
          qrCodeDataUrl={qrCodeDataUrl}
          assinaturaDataUrl={assinaturaDataUrl}
          assinaturaMensagem={assinaturaMensagem}
        />
      );

      const blob = await pdf(doc).toBlob();
      await isencaoLicenciamentoConsultaService.emitir(
        isencao.id,
        blob,
        isencao.numeroProcesso ? undefined : numeroInformado.trim(),
      );

      setSnackbar({
        open: true,
        message: "Isenção emitida. Aguardando assinatura do secretário.",
        severity: "success",
      });
      onStatusAtualizado?.();
    } catch (err) {
      const msg = getApiErrorMessage(err, "Erro ao emitir isenção.");
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setEmitindo(false);
    }
  };

  const handleBaixarEmitida = async () => {
    setBaixandoEmitida(true);
    try {
      const data = await isencaoLicenciamentoConsultaService.baixarDocumentoEmitido(isencao.id);
      const byteArray = Uint8Array.from(atob(data.conteudo), (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setSnackbar({ open: true, message: "Erro ao baixar isenção emitida.", severity: "error" });
    } finally {
      setBaixandoEmitida(false);
    }
  };

  if (!isencao) return null;

  const statusColor = STATUS_COLORS[isencao.status] ?? "default";
  const statusLabel = statusIsencaoLabels[isencao.status] ?? "Desconhecido";
  const podeAtualizar = STATUS_DECISAO_PERMITIDOS.includes(isencao.status);
  const podeEmitir = isencao.status === statusIsencaoEnum.DEFERIDO && !(isencao.isencaoEmitida || isencao.isencaoEmitidaS3Key);
  const jaEmitida = !!(isencao.isencaoEmitida || isencao.isencaoEmitidaS3Key);
  const podeVisualizarEmitida =
    jaEmitida &&
    (isencao.status === statusIsencaoEnum.ASSINADA || isencao.assinadoDigitalmente === true);
  const aguardandoAssinatura =
    jaEmitida && isencao.status === statusIsencaoEnum.AGUARDANDO_ASSINATURA;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AssignmentIcon color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={600} lineHeight={1.3}>
                Isenção de Licenciamento — Analista
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Detalhes e ações do requerimento
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <BusinessIcon sx={{ color: "text.secondary", mt: 0.25, fontSize: 20, flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
                  Empreendimento
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {isencao.empreendimentoNome || "—"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <PersonIcon sx={{ color: "text.secondary", mt: 0.25, fontSize: 20, flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
                  Solicitante
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {isencao.solicitanteNome || "Não especificado"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <AssignmentIcon sx={{ color: "text.secondary", mt: 0.25, fontSize: 20, flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
                  Atividade
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {atividade || "—"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <CalendarIcon sx={{ color: "text.secondary", mt: 0.25, fontSize: 20, flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
                  Data da Solicitação
                </Typography>
                <Typography variant="body1">
                  {isencao.createAt
                    ? new Date(isencao.createAt).toLocaleString("pt-BR")
                    : "—"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box sx={{ width: 20, flexShrink: 0, display: "flex", justifyContent: "center", mt: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: `${statusColor === "default" ? "grey.400" : `${statusColor}.main`}` }} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
                  Status
                </Typography>
                <Box>
                  <Chip label={statusLabel} color={statusColor} size="small" sx={{ mt: 0.25 }} />
                </Box>
              </Box>
            </Box>
          </Box>

          {isencao.status === statusIsencaoEnum.INDEFERIDO && isencao.motivoIndeferimento && (
            <Alert severity="error" sx={{ mx: 3, mb: 2 }}>
              <strong>Motivo do Indeferimento:</strong> {isencao.motivoIndeferimento}
            </Alert>
          )}

          <Divider />

          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={0.8}>
              Documentos
            </Typography>
          </Box>

          <List disablePadding>
            {DOCUMENTOS.map(({ key, tipo, label }, index) => {
              const enviado = !!isencao[key];
              const isLoading = loadingTipo === tipo;
              return (
                <ListItem key={key} divider={index < DOCUMENTOS.length - 1} sx={{ px: 3, py: 1.25 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {enviado ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <MissingIcon color="disabled" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={enviado ? 500 : 400} color={enviado ? "text.primary" : "text.disabled"}>
                        {label}
                      </Typography>
                    }
                  />
                  {enviado && (
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={isLoading ? <CircularProgress size={12} color="inherit" /> : <OpenInNewIcon fontSize="small" />}
                        disabled={!!loadingTipo}
                        onClick={() => handleVisualizarDocumento(tipo)}
                      >
                        {isLoading ? "Abrindo..." : "Abrir"}
                      </Button>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              );
            })}
          </List>

          <Box sx={{ px: 3, pb: 2 }}>
            <PagamentosSection
              solicitacao={isencao}
              papel={PAPEL_ANALISTA}
              limiteUmBoleto
            />
          </Box>

          {podeAtualizar && (
            <>
              <Divider sx={{ mt: 1 }} />
              <Box sx={{ px: 3, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={0.8}>
                  Status de Pagamento
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                  <TextField
                    select
                    size="small"
                    label="Alterar status"
                    value={statusPagamento}
                    onChange={(e) => setStatusPagamento(e.target.value)}
                    sx={{ minWidth: 240 }}
                    disabled={processando}
                  >
                    <MenuItem value={statusIsencaoEnum.AGUARDANDO_PAGAMENTO}>
                      Aguardando Pagamento
                    </MenuItem>
                    <MenuItem value={statusIsencaoEnum.PAGAMENTO_EFETUADO}>
                      Pagamento Efetuado
                    </MenuItem>
                  </TextField>
                  <Button
                    variant="outlined"
                    disabled={processando || statusPagamento === ""}
                    onClick={handleAtualizarStatusPagamento}
                  >
                    Atualizar
                  </Button>
                </Box>

                <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={0.8}>
                  Decisão
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={processando}
                    onClick={handleDeferir}
                    startIcon={processando && !mostrarIndeferimento ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {processando && !mostrarIndeferimento ? "Deferindo..." : "Deferir"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={processando}
                    onClick={() => setMostrarIndeferimento((v) => !v)}
                  >
                    {mostrarIndeferimento ? "Cancelar Indeferimento" : "Indeferir"}
                  </Button>
                </Box>

                {mostrarIndeferimento && (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Motivo do Indeferimento"
                      value={motivo}
                      onChange={(e) => {
                        setMotivo(e.target.value);
                        if (erroMotivo) setErroMotivo("");
                      }}
                      inputProps={{ maxLength: MOTIVO_MAX }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ alignItems: "flex-end", pb: 1 }}>
                            <CharCounter value={motivo} maxLength={MOTIVO_MAX} />
                          </InputAdornment>
                        ),
                      }}
                      error={!!erroMotivo}
                      helperText={erroMotivo || " "}
                    />
                    <Button
                      variant="contained"
                      color="error"
                      disabled={processando}
                      onClick={handleIndeferir}
                      startIcon={processando && mostrarIndeferimento ? <CircularProgress size={16} color="inherit" /> : null}
                      sx={{ mt: 1 }}
                    >
                      {processando && mostrarIndeferimento ? "Indeferindo..." : "Confirmar Indeferimento"}
                    </Button>
                  </Box>
                )}
              </Box>
            </>
          )}

          {(podeEmitir || jaEmitida) && (
            <>
              <Divider />
              <Box sx={{ px: 3, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={0.8}>
                  Emissão
                </Typography>
                {podeEmitir && (
                  <>
                    {isencao.numeroProcesso ? (
                      <TextField
                        label="Número do Processo"
                        value={isencao.numeroProcesso}
                        disabled
                        fullWidth
                      />
                    ) : (
                      <NumeroProcessoField
                        prefixo={PREFIXO_ISENCAO}
                        value={numeroInformado}
                        onChange={setNumeroInformado}
                        ano={anoProcesso}
                        error={Boolean(numeroInformado) && !ehNumeroInformadoValido(numeroInformado)}
                        helperText={
                          Boolean(numeroInformado) && !ehNumeroInformadoValido(numeroInformado)
                            ? "Informe apenas dígitos (1 a 10)."
                            : undefined
                        }
                      />
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={emitindo ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
                      disabled={
                        emitindo ||
                        (!isencao.numeroProcesso && !ehNumeroInformadoValido(numeroInformado))
                      }
                      onClick={handleEmitir}
                    >
                      {emitindo ? "Gerando PDF..." : "Emitir Isenção de Licenciamento"}
                    </Button>
                  </>
                )}
                {aguardandoAssinatura && (
                  <Alert severity="info">
                    Documento emitido. Aguardando assinatura digital do secretário.
                    A visualização só estará disponível após a assinatura.
                  </Alert>
                )}
                {podeVisualizarEmitida && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={baixandoEmitida ? <CircularProgress size={16} color="inherit" /> : <OpenInNewIcon />}
                    disabled={baixandoEmitida}
                    onClick={handleBaixarEmitida}
                  >
                    {baixandoEmitida ? "Abrindo..." : "Visualizar Isenção Emitida"}
                  </Button>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined" color="inherit">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default IsencaoAnalistaModal;
